
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// Define an interface for the client data returned from Supabase
export interface ClientData {
  user_id: string;
  id: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  company?: string;
  language?: string;
  language_secondary?: string;
  country?: string;
  created_at?: string | null;
  journal_count?: number;
  is_placeholder?: boolean;
}

// Function to get all clients with detailed information
export async function getAllClients(): Promise<ClientData[]> {
  try {
    console.log("Starting getAllClients function using secure function...");
    
    // Use the new security definer function to fetch client data
    const { data, error } = await supabase
      .rpc('get_admin_clients');
      
    if (error) {
      console.error("Error fetching admin clients:", error);
      toast.error("Failed to fetch client data: " + error.message);
      
      // Fall back to the analytics data if the direct query fails
      return fallbackToAnalyticsData();
    }
    
    if (!data || !Array.isArray(data)) {
      console.error("Invalid data format received:", data);
      return fallbackToAnalyticsData();
    }
    
    console.log("Client data retrieved successfully:", data.length, "clients found");
    
    // Properly cast and transform the data to ensure it matches ClientData interface
    const clientData = data.map(item => {
      // Since we're working with JSON data, we need to safely access properties
      const jsonItem = item as Record<string, any>;
      
      return {
        user_id: String(jsonItem.user_id || jsonItem.id || 'unknown'),
        id: String(jsonItem.id || jsonItem.user_id || 'unknown'),
        email: jsonItem.email as string | undefined,
        first_name: jsonItem.first_name as string | undefined,
        last_name: jsonItem.last_name as string | undefined,
        company: jsonItem.company as string | undefined,
        language: jsonItem.language as string | undefined,
        language_secondary: jsonItem.language_secondary as string | undefined,
        country: jsonItem.country as string | undefined,
        created_at: jsonItem.created_at as string | null,
        journal_count: typeof jsonItem.journal_count === 'number' ? jsonItem.journal_count : 0,
        is_placeholder: false
      } as ClientData;
    });
    
    return clientData;
  } catch (error) {
    console.error("Exception in getAllClients:", error);
    toast.error("An error occurred while fetching client data");
    
    // Fall back to analytics data in case of any exceptions
    return fallbackToAnalyticsData();
  }
}

// Helper function to create client placeholders from analytics data as a fallback
async function fallbackToAnalyticsData(): Promise<ClientData[]> {
  console.log("Falling back to analytics data for client information...");
  
  try {
    // Get analytics data which contains client count
    const { data: analyticsData, error: analyticsError } = await supabase
      .rpc('get_admin_analytics');
      
    if (analyticsError || !analyticsData) {
      console.error("Error fetching admin analytics for fallback:", analyticsError);
      return [];
    }
    
    const clientCount = (analyticsData as any)?.clientCount || 0;
    console.log("Analytics data shows", clientCount, "clients");
    
    if (clientCount <= 0) {
      return [];
    }
    
    // Create placeholder objects for clients based on analytics data
    const placeholders: ClientData[] = [];
    
    for (let i = 0; i < clientCount; i++) {
      placeholders.push({
        user_id: `placeholder-${i}`,
        id: `placeholder-${i}`,
        email: "Data unavailable",
        first_name: "Client",
        last_name: `#${i+1}`,
        country: "Unknown",
        language: "Unknown",
        journal_count: 0,
        created_at: new Date().toISOString(),
        is_placeholder: true
      });
    }
    
    console.log(`Created ${placeholders.length} client placeholders as fallback`);
    return placeholders;
  } catch (error) {
    console.error("Error in fallback function:", error);
    return [];
  }
}
